import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { FileText, Trash2, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

const LogIcon = ({ level }) => {
  switch (level?.toUpperCase()) {
    case 'ERROR':
      return <AlertCircle className="h-4 w-4 text-red-400" />;
    case 'WARN':
    case 'WARNING':
      return <AlertCircle className="h-4 w-4 text-amber-400" />;
    case 'SUCCESS':
      return <CheckCircle className="h-4 w-4 text-emerald-400" />;
    default:
      return <Info className="h-4 w-4 text-blue-400" />;
  }
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  try {
    const date = new Date(ts);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return ts;
  }
};

export const LogView = ({ logs = [], onClear, loading = false }) => {
  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="flex items-center gap-2 font-heading">
          <FileText className="h-5 w-5" />
          Journal d'activité
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={onClear}
          data-testid="clear-logs-button"
          className="border-border/50 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Effacer
        </Button>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p>Aucune entrée dans le journal</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, index) => (
                <div
                  key={log.id || index}
                  data-testid={`log-entry-${index}`}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-md bg-background/50',
                    'border border-transparent hover:border-border/50 transition-colors'
                  )}
                >
                  <LogIcon level={log.level} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={cn(
                        'text-xs font-medium px-1.5 py-0.5 rounded',
                        log.level === 'ERROR' && 'bg-red-500/10 text-red-400',
                        log.level === 'WARN' && 'bg-amber-500/10 text-amber-400',
                        log.level === 'INFO' && 'bg-blue-500/10 text-blue-400',
                        log.level === 'SUCCESS' && 'bg-emerald-500/10 text-emerald-400'
                      )}>
                        {log.action || log.level}
                      </span>
                      <span className="text-xs text-muted-foreground font-mono">
                        {formatTimestamp(log.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-foreground break-words">
                      {log.detail || 'Aucun détail'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default LogView;
